import {
  configureStore,
  combineReducers,
  Middleware,
  isRejectedWithValue,
} from "@reduxjs/toolkit";
import {
  useDispatch,
  TypedUseSelectorHook,
  useSelector,
  useStore,
} from "react-redux";
import {
  baseApi,
  baseApiMiddleware,
  baseApiReducer,
} from "../services/baseApi";
import { reducers } from "../reducers/reducers";
import { logout } from "../slices/authSlice";
import { trackEvents } from "../../utils/amplitude/amplitude";
import { captureApiError } from "../../observability/sentry/sentry.utils";
import { clearSentryUser } from "../../observability/sentry/sentry.identity";

const rtkQueryErrorLogger: Middleware = (storeApi) => (next) => (action) => {
  if (isRejectedWithValue(action)) {
    const state = storeApi.getState() as RootState;
    const userId = state?.auth?.user?._id || "unknown";
    const orgId = state?.auth?.client?.client_code || "unknown";

    const payload = action.payload as Record<string, unknown> | undefined;
    const statusCode = (payload?.status as number) ?? 0;
    const errorData = (payload?.data ?? {}) as Record<string, unknown>;
    const errorDetail =
      (errorData?.detail as string) ||
      (errorData?.message as string) ||
      "Unknown error";
    const endpointName =
      (action?.meta as Record<string, unknown>)?.arg &&
      typeof (action.meta as Record<string, unknown>).arg === "object"
        ? (
            (action.meta as Record<string, unknown>).arg as Record<
              string,
              unknown
            >
          )?.endpointName
        : undefined;

    trackEvents("api_error", {
      endpoint: (endpointName as string) || "unknown",
      status_code: statusCode,
      error_message: errorDetail,
      url: window.location.pathname,
      user_id: userId,
      org_id: orgId,
    });

    captureApiError({
      endpoint: (endpointName as string) || "unknown",
      status: statusCode,
      detail: errorDetail,
      userId,
      orgId,
      url: window.location.pathname,
    });

    let errorMessage: string;


    if (
      action.payload &&
      typeof action.payload === "object" &&
      "data" in action.payload &&
      action.payload.data &&
      typeof action.payload.data === "object"
    ) {
      if (errorData.error_id && errorData.type === "auth_error") {
        clearSentryUser();
        next(logout());
        return;
      }

      if (
        "detail" in errorData &&
        "status" in errorData &&
        "status" in action.payload &&
        typeof (errorData as { status: number }).status === "number" &&
        typeof action.payload.status === "number"
      ) {
        if (
          (errorData as { status: number; detail: string }).status === 401 ||
          action.payload.status === 401
        ) {
          const appearanceMode = localStorage.getItem("appearanceMode");
          const isDarkMode = localStorage.getItem("isDarkMode");
          localStorage.clear();
          window.sessionStorage.clear();
          if (appearanceMode) {
            localStorage.setItem("appearanceMode", appearanceMode);
          }
          if (isDarkMode) {
            localStorage.setItem("isDarkMode", isDarkMode);
          }
          errorMessage = (errorData as { detail: string }).detail;
        } else {
          errorMessage = "Something went wrong, try again later.";
        }
        console.log(errorMessage, "error");
      } else {
        errorMessage = "Something went wrong, try again later.";
      }
    } else {
      errorMessage = "Something went wrong, try again later.";
    }
  }

  return next(action);
};

const appReducer = combineReducers({
  ...reducers,
  [baseApi.reducerPath]: baseApiReducer,
});

const rootReducer: typeof appReducer = (state, action) => {
  if (action.type === logout.type) {
    state = undefined;
  }
  return appReducer(state, action);
};

export const makeStore = () => {
  return configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({ serializableCheck: false }).concat(
        baseApiMiddleware,
        rtkQueryErrorLogger,
      ),
  });
};

export type AppStore = ReturnType<typeof makeStore>;
export type RootState = ReturnType<AppStore["getState"]>;
export type AppDispatch = AppStore["dispatch"];

export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
export const useAppStore: () => AppStore = useStore;
